export type CanvasElement = {
  id?: string;
  type?: string;
  content?: string;
  attachedToElement?: string;
  c4?: {
    kind?: string;
  };
};

export type C4DeploymentNode = {
  id?: string;
  kind: string;
  label: string;
  technology: string;
  description: string;
  name: string;
  terraformName: string;
};

export type C4ServiceNode = C4DeploymentNode;

export type C4DatabaseNode = C4DeploymentNode & {
  supportedEngine: 'postgres' | null;
  unsupportedReason?: string;
};

export type C4UnsupportedNode = C4DeploymentNode & {
  unsupportedReason: string;
};

export type C4DeploymentModel = {
  diagramName: string;
  namespace: string;
  services: C4ServiceNode[];
  databases: C4DatabaseNode[];
  unsupported: C4UnsupportedNode[];
};

const DEPLOYABLE_KINDS = new Set(['container', 'microservice', 'webapp', 'api-service']);
const IGNORED_KINDS = new Set(['person', 'external-person', 'system', 'external-system']);
const DNS_LABEL_MAX = 63;

const firstNonEmptyLine = (value: string | undefined): string | null => {
  const line = (value || '').split('\n').map((part) => part.trim()).find(Boolean);
  return line || null;
};

const textLinesFor = (elements: CanvasElement[], elementId: string | undefined): string[] => {
  const textElement = elements.find((element) =>
    element.type === 'text' && element.attachedToElement === elementId
  );
  const parts = (textElement?.content || '').split('\n');
  return [parts[0] || '', parts[1] || '', parts[2] || ''].map((part) => part.trim());
};

const technologyFromLine = (line: string): string => {
  const match = line.match(/\[(?:[^:\]]+):\s*([^\]]+)\]/);
  return match ? match[1].trim() : '';
};

const toDnsLabel = (value: string): string => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, DNS_LABEL_MAX)
    .replace(/-+$/g, '');
  return slug;
};

const terraformNameFromDnsName = (name: string): string => {
  const normalized = name.replace(/-/g, '_');
  return /^[a-z_]/.test(normalized) ? normalized : `r_${normalized}`;
};

const dedupe = (baseName: string, used: Set<string>): string => {
  let name = baseName;
  let index = 2;
  while (used.has(name)) {
    const suffix = `-${index}`;
    name = `${baseName.slice(0, DNS_LABEL_MAX - suffix.length).replace(/-+$/g, '')}${suffix}`;
    index += 1;
  }
  used.add(name);
  return name;
};

const postgresEngineFor = (technology: string): 'postgres' | null => {
  const normalized = technology.toLowerCase();
  return normalized.includes('postgres') || normalized.includes('postgresql') ? 'postgres' : null;
};

const buildNode = (
  elements: CanvasElement[],
  element: CanvasElement,
  fallbackPrefix: string,
  fallbackIndex: number,
  usedNames: Set<string>,
): C4DeploymentNode => {
  const [rawLabel, rawTechnologyLine, rawDescription] = textLinesFor(elements, element.id);
  const kind = element.c4?.kind || 'unknown';
  const label = firstNonEmptyLine(rawLabel) || `${fallbackPrefix}-${fallbackIndex}`;
  const baseName = toDnsLabel(label) || `${fallbackPrefix}-${fallbackIndex}`;
  const name = dedupe(baseName, usedNames);

  return {
    id: element.id,
    kind,
    label,
    technology: technologyFromLine(rawTechnologyLine),
    description: rawDescription,
    name,
    terraformName: terraformNameFromDnsName(name),
  };
};

export const extractC4DeploymentModel = (
  elements: CanvasElement[],
  diagramName = 'architecture',
): C4DeploymentModel => {
  const usedNames = new Set<string>();
  const namespace = toDnsLabel(diagramName) || 'app';
  const c4Elements = elements.filter((element) => element.type === 'rectangle' && element.c4?.kind);

  const services: C4ServiceNode[] = [];
  const databases: C4DatabaseNode[] = [];
  const unsupported: C4UnsupportedNode[] = [];
  let serviceIndex = 1;
  let databaseIndex = 1;
  let unsupportedIndex = 1;

  c4Elements.forEach((element) => {
    const kind = element.c4?.kind || 'unknown';

    if (DEPLOYABLE_KINDS.has(kind)) {
      services.push(buildNode(elements, element, 'service', serviceIndex, usedNames));
      serviceIndex += 1;
      return;
    }

    if (kind === 'database') {
      const database = buildNode(elements, element, 'database', databaseIndex, usedNames);
      const supportedEngine = postgresEngineFor(database.technology);
      databases.push({
        ...database,
        supportedEngine,
        unsupportedReason: supportedEngine
          ? undefined
          : `Unsupported database technology: ${database.technology || 'unspecified'}`,
      });
      databaseIndex += 1;
      return;
    }

    if (!IGNORED_KINDS.has(kind)) {
      const node = buildNode(elements, element, 'element', unsupportedIndex, usedNames);
      unsupported.push({
        ...node,
        unsupportedReason: `C4 kind "${kind}" is not deployable by the MVP generator.`,
      });
      unsupportedIndex += 1;
    }
  });

  return {
    diagramName,
    namespace,
    services,
    databases,
    unsupported,
  };
};
